import React, { useState } from "react";
import {
  Grid,
  TextField,
  Button,
  Paper,
  Typography,
  InputAdornment,
  Divider,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { Search as SearchIcon } from "@material-ui/icons";
import PersonIcon from "@material-ui/icons/Person";
import { API } from "aws-amplify";
import { IPatientDetails } from "../types/PrescriptionIngestionTypes";
import { COLORS } from "../styles/theme";
import { parseMailingAddress } from "../utils/AddressParser";

interface ICustomerRetrievalSectionProps {
  onCustomerRetrieved: (patientDetails: IPatientDetails) => void;
  // Optionally surfaces the matched customer's identifiers (when available) so a
  // caller can carry them forward; separate from the demographic auto-fill above.
  onIdsRetrieved?: (ids: { personId?: string; profileId?: string }) => void;
  onSuccess?: () => void;
}

enum SearchStatus {
  NoQuery,
  Searching,
  ResultsFound,
  NotFound,
  Error,
}

const CustomerRetrievalSection: React.FC<ICustomerRetrievalSectionProps> = ({
  onCustomerRetrieved,
  onIdsRetrieved,
  onSuccess,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [status, setStatus] = useState<SearchStatus>(SearchStatus.NoQuery);

  const handleSearchTermChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearchTerm(event.target.value);
    // Reset status when user starts typing again
    if (status !== SearchStatus.NoQuery) {
      setStatus(SearchStatus.NoQuery);
    }
  };

  const mapCustomerToPatientDetails = (
    customer: any,
    addressData?: any
  ): IPatientDetails => {
    // Extract data from customer response
    const pharmacyCustomer = customer?.pharmacyCustomer || {};
    const profiles = pharmacyCustomer?.profile || [];
    const firstProfile = profiles[0] || {};
    const demographicProfile = firstProfile?.demographicProfile || {};

    // Parse address from mailingAddress string if available
    let parsedAddress = {
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      zipCode: "",
    };

    if (addressData?.mailingAddress) {
      parsedAddress = parseMailingAddress(addressData.mailingAddress);
    }

    // Map gender - API returns "Male"/"Female", we need "M"/"F"
    let gender = "M";
    if (demographicProfile?.gender) {
      gender = demographicProfile.gender.charAt(0).toUpperCase();
    }

    return {
      lastName: demographicProfile?.legalLastName || "",
      firstName: demographicProfile?.legalFirstName || "",
      gender: gender,
      dateOfBirth: demographicProfile?.dateOfBirth || "",
      addressLine1: parsedAddress.addressLine1,
      addressLine2: parsedAddress.addressLine2,
      city: parsedAddress.city,
      state: parsedAddress.state,
      zipCode: parsedAddress.zipCode,
      phoneNumber: pharmacyCustomer?.phoneNumber || "",
    };
  };

  const handleSubmit = async () => {
    if (!searchTerm.trim()) {
      return;
    }

    setStatus(SearchStatus.Searching);

    try {
      const apiName = "ApaqPortalAPI";
      const searchPath = "/pharmacy-customer/search/" + searchTerm.trim();
      const config = { headers: {} };

      const customerResponse = await API.get(apiName, searchPath, config);
      console.log("Customer search response:", customerResponse);

      // Extract personId from the response
      const profiles = customerResponse?.pharmacyCustomer?.profile || [];
      const firstProfile = profiles[0];
      const personId = firstProfile?.personId;

      // Surface the matched customer's identifiers for callers that carry them
      // forward (e.g. the NewRx Operations step handoff).
      if (onIdsRetrieved) {
        onIdsRetrieved({ personId, profileId: firstProfile?.profileId });
      }

      let addressData = null;

      // Fetch address if personId is available
      if (personId) {
        try {
          const addressPath = `/customer/address/get-addresses?personId=${personId}`;
          const addressResponse = await API.get(apiName, addressPath, config);
          console.log("Address response:", addressResponse);

          // Get the first address from the response
          if (
            addressResponse?.customerAddresses &&
            addressResponse.customerAddresses.length > 0
          ) {
            addressData = addressResponse.customerAddresses[0];
          }
        } catch (addressError) {
          console.warn(
            "Failed to fetch address, continuing without it:",
            addressError
          );
          // Continue without address data
        }
      }

      setStatus(SearchStatus.ResultsFound);

      // Map customer data to patient details and notify parent
      const patientDetails = mapCustomerToPatientDetails(
        customerResponse,
        addressData
      );
      onCustomerRetrieved(patientDetails);

      // Call onSuccess callback if provided
      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
        }, 300);
      }
    } catch (error) {
      console.error("Customer search error:", error);
      setStatus(SearchStatus.NotFound);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleSubmit();
    }
  };

  return (
    <Grid item xs={12}>
      <Paper
        style={{ padding: 24, backgroundColor: "#f5f5f5", borderRadius: 8 }}
      >
        <Typography
          variant="h6"
          gutterBottom
          style={{
            color: "black",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <PersonIcon style={{ color: "#546e7a" }} />
          Retrieve Existing Customer (Optional)
        </Typography>
        <Typography
          variant="caption"
          color="textSecondary"
          style={{ display: "block", marginBottom: 16 }}
        >
          Search for an existing Amazon Pharmacy customer to auto-fill patient
          details
        </Typography>
        <Divider style={{ marginBottom: 20 }} />

        <Alert severity="info" style={{ marginBottom: 16 }}>
          <Typography variant="body2" style={{ fontSize: 13 }}>
            <strong>Note:</strong> Customers created on different APAQ Portal
            stages cannot be retrieved across stages. For example, customers
            created on Alpha can only be retrieved on Alpha, not on Beta.
          </Typography>
        </Alert>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              value={searchTerm}
              onChange={handleSearchTermChange}
              onKeyPress={handleKeyPress}
              placeholder="Search: Email, CustomerId, ProfileId or PersonId"
              disabled={status === SearchStatus.Searching}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon style={{ color: COLORS.textSecondary }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSubmit}
              disabled={status === SearchStatus.Searching || !searchTerm.trim()}
              startIcon={<SearchIcon />}
              style={{
                backgroundColor: COLORS.orange,
                color: "#000000",
                textTransform: "none",
                fontWeight: 600,
                fontSize: 16,
                padding: "10px 24px",
                borderRadius: 8,
              }}
            >
              {status === SearchStatus.Searching
                ? "Searching..."
                : "Search Customer"}
            </Button>
          </Grid>
        </Grid>

        {status === SearchStatus.ResultsFound && (
          <Alert
            severity="success"
            style={{ marginTop: 16 }}
            onClose={() => setStatus(SearchStatus.NoQuery)}
          >
            <strong>Customer Found!</strong> Patient details have been
            auto-filled below. You can modify them if needed.
          </Alert>
        )}

        {status === SearchStatus.NotFound && (
          <Alert
            severity="warning"
            style={{ marginTop: 16 }}
            onClose={() => setStatus(SearchStatus.NoQuery)}
          >
            <Typography
              variant="body2"
              style={{ fontWeight: "bold", marginBottom: 4 }}
            >
              Customer Not Found
            </Typography>
            <Typography variant="body2">
              No customer data was found for: <strong>"{searchTerm}"</strong>
            </Typography>
            <Typography variant="body2" style={{ marginTop: 8 }}>
              Please verify and try again. You can search by Email, Customer ID,
              Profile ID, or Person ID.
            </Typography>
          </Alert>
        )}
      </Paper>
    </Grid>
  );
};

export default CustomerRetrievalSection;
